import { styled } from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';

import { useApp } from 'contexts/app';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { useWebsocket } from 'contexts/websocket';
import { PageContext } from 'pages/Teams/PageProvider';
import { Box, Button, IconButton, Radio, RadioGroup, ResponsiveDialog, Stack, Text, TextInput } from 'shell/components';
import { numberRegex } from 'utils/regex';
import { isNatural } from 'utils/helper';

const CreateFromScratch = () => {
    const { message } = useSnackbar();
    const { course, groups, studentAccountUsers } = useApp();
    const { device } = useViewport();
    const { sendOn } = useWebsocket();

    const { setIsWSLoading, isCreateFromScratchOpen, setIsCreateFromScratchOpen, setEditTeams } =
        useContext(PageContext);

    const groupSizeInputRef = useRef(null);
    const groupNumInputRef = useRef(null);

    const [selected, setSelected] = useState('group-size');
    const [groupSizeValue, setGroupSizeValue] = useState('');
    const [groupNumValue, setGroupNumValue] = useState('');
    const [isIndividual, setIsIndividual] = useState(false);

    const handleClick = () => {
        if (
            (selected === 'group-size' && !isNatural(groupSizeValue)) ||
            (selected === 'group-num' && !isNatural(groupNumValue))
        ) {
            return message.error('Please enter a valid number for the selected option.');
        }
        setIsCreateFromScratchOpen(false);
        setEditTeams({
            open: true,
            type: selected,
            groupSize: isIndividual ? 1 : groupSizeValue,
            numGroups: groupNumValue,
            groups,
            studentAccountUsers,
            onSubmit: ({ teamChanges, studentChanges }) => {
                setIsWSLoading(true);
                sendOn('default', {
                    payload_type: 'update_user_teams',
                    payload: {
                        course_id: course.id,
                        team_changes: teamChanges,
                        student_changes: studentChanges,
                    },
                });
            },
        });
    };

    useEffect(() => {
        if (selected === 'group-size' && groupSizeInputRef.current) groupSizeInputRef.current.focus();
        if (selected === 'group-num' && groupNumInputRef.current) groupNumInputRef.current.focus();
        if (selected === 'individual-size') setIsIndividual(true);
        else setIsIndividual(false);
    }, [selected]);

    return (
        <ResponsiveDialog open={isCreateFromScratchOpen} onClose={() => setIsCreateFromScratchOpen(false)}>
            <CustomStack device={device} spacing={7}>
                <Box>
                    <Stack direction={'row'} justifyContent={'space-between'}>
                        <Text align={'center'} variant={'h6'}>
                            Create Teams from Scratch
                        </Text>
                        <IconButton
                            aria-label={'close'}
                            kind={'exit'}
                            size={'medium'}
                            variant={'square'}
                            onClick={() => setIsCreateFromScratchOpen(false)}
                        />
                    </Stack>
                    <Stack direction={'row'} spacing={1}>
                        <Text variant={'p2'}>Class size:</Text>
                        <Text color={'primary'} variant={'p2'}>
                            {studentAccountUsers.length} students
                        </Text>
                    </Stack>
                </Box>
                <Stack>
                    <RadioGroup sx={{ gap: 2 }} value={selected} onChange={(e) => setSelected(e.target.value)}>
                        <Radio
                            value={'group-size'}
                            label={
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    Randomize students into teams of
                                    <CustomTextInput
                                        autoFocus
                                        inputRef={groupSizeInputRef}
                                        placeholder={'e.g. 4'}
                                        value={groupSizeValue}
                                        onChange={(e) => {
                                            if (!e.target.value || numberRegex.test(e.target.value)) {
                                                setGroupSizeValue(e.target.value);
                                            }
                                        }}
                                    />
                                </Stack>
                            }
                        />
                        <Radio
                            value={'group-num'}
                            label={
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    Randomize students into
                                    <CustomTextInput
                                        inputRef={groupNumInputRef}
                                        placeholder={'e.g. 4'}
                                        value={groupNumValue}
                                        onChange={(e) => {
                                            if (!e.target.value || numberRegex.test(e.target.value)) {
                                                setGroupNumValue(e.target.value);
                                            }
                                        }}
                                    />
                                    teams
                                </Stack>
                            }
                        />
                        <Radio
                            value={'individual-size'}
                            label={
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    Randomize students into teams of 1
                                </Stack>
                            }
                        />
                        <Radio value={'manual'} label={'I’ll create my own teams manually'} />
                    </RadioGroup>
                </Stack>
                <Stack direction={'row-reverse'} spacing={4}>
                    <Button onClick={handleClick}>Confirm</Button>
                    <Button color={'neutral'} variant={'tertiary'} onClick={() => setIsCreateFromScratchOpen(false)}>
                        Cancel
                    </Button>
                </Stack>
            </CustomStack>
        </ResponsiveDialog>
    );
};

const CustomStack = styled(Stack)(({ device }) => ({
    width: '35.25rem',
    ...(device === 'mobile' && {
        width: '100%',
    }),
}));

const CustomTextInput = styled(TextInput)(() => ({
    width: '4rem',
}));

export default CreateFromScratch;
